import { A, useParams } from '@solidjs/router';
import { For, Show, createEffect, createResource, createSignal, onCleanup } from 'solid-js';
import { api, BillingProfile, Customer, Device, Zone, daypartName, fmtTime } from '../api';
import { ConfirmButton, ErrorLine, PageHeader, StatusPill, useAction } from '../ui';
import CustomerStations from './CustomerStations';
import CustomerTape from './CustomerTape';
import PairingPanel from './PairingPanel';
import Users from './Users';

export default function CustomerPage() {
  const p = useParams();
  const [customer, { mutate: setCustomer }] = createResource(() => p.id, (id) => api<Customer>(`/customers/${id}`));
  const [zones, { refetch: refetchZones }] = createResource(() => p.id, (id) => api<Zone[]>(`/customers/${id}/zones`));
  const [devices, { refetch: refetchDevices }] = createResource(() => p.id, (id) => api<Device[]>(`/customers/${id}/devices`));
  const [shown, setShown] = createSignal<string | null>(null); // device whose pairing panel is open
  const shownDevice = () => devices()?.find((x) => x.id === shown());
  // every 3 s while a code is waiting (so the admin sees the device pair), else every 15 s
  let tick = 0;
  const timer = setInterval(() => {
    tick++;
    const waiting = devices()?.some((x) => x.pairing_code) || (shownDevice() && !shownDevice()!.paired);
    if (waiting || tick % 5 === 0) refetchDevices();
  }, 3000);
  onCleanup(() => clearInterval(timer));
  const renew = (id: string) => d.run(async () => {
    await api(`/devices/${id}/pairing-code`, { method: 'POST' });
    setShown(id); refetchDevices();
  });
  const [zoneName, setZoneName] = createSignal('');
  const [pairZone, setPairZone] = createSignal('');
  const [pairName, setPairName] = createSignal('');
  const z = useAction();
  const d = useAction();
  const admin = () => ['operator', 'partner_admin', 'customer_admin'].includes(customer()?.role ?? '');
  const zoneOf = (id: string | null) => zones()?.find((x) => x.id === id)?.name ?? '—';
  const manager = () => ['operator', 'partner_admin'].includes(customer()?.role ?? ''); // tier and status
  const live = () => ['active', 'trial'].includes(customer()?.status ?? 'active');
  const [editing, setEditing] = createSignal<string | null>(null); // device id being edited
  const [devName, setDevName] = createSignal('');
  const [devZone, setDevZone] = createSignal('');
  const editDevice = (dv: Device) => { setEditing(dv.id); setDevName(dv.name); setDevZone(dv.zone_id ?? ''); };
  const saveDevice = (dv: Device) => d.run(async () => {
    await api(`/devices/${dv.id}`, { method: 'PATCH', json: { name: devName(), zone_id: devZone() !== dv.zone_id ? devZone() : undefined } });
    setEditing(null); refetchDevices();
  });
  const music = (zn: Zone) => {
    if (zn.follows_zone_id) return `Follows ${zoneOf(zn.follows_zone_id)}`;
    const dps = Object.keys(zn.genre_mix ?? {}).filter((x) => x !== 'default');
    const base = zn.playlist_kind === 'manual' ? `Hand-picked · ${zn.playlist.length} tracks`
      : zn.playlist_kind === 'auto' ? 'Genre mix' : 'Nothing set';
    return [base, dps.length ? `${dps.map(daypartName).join(', ')} daypart${dps.length > 1 ? 's' : ''}` : '', zn.schedule.length ? 'opening hours' : '']
      .filter(Boolean).join(' · ');
  };
  const zoneState = (zn: Zone): ['ok' | 'warn' | 'off', string] => {
    if (!live()) return ['off', customer()?.status === 'closed' ? 'Closed' : 'Suspended'];
    if (zn.status !== 'active') return ['warn', zn.status === 'paused' ? 'Paused' : zn.status];
    return zn.follows_zone_id || zn.playlist_kind ? ['ok', 'Playing'] : ['warn', 'Silent: no music set'];
  };

  const createZone = (e: Event) => {
    e.preventDefault();
    z.run(async () => {
      await api(`/customers/${p.id}/zones`, { method: 'POST', json: { name: zoneName() } });
      setZoneName(''); refetchZones();
    });
  };
  const pair = (e: Event) => {
    e.preventDefault();
    d.run(async () => {
      const r = await api<{ device_id: string }>(`/customers/${p.id}/devices/pair`, { method: 'POST',
        json: { zone_id: pairZone() || zones()?.[0]?.id, name: pairName() || 'New device' } });
      setPairName(''); setShown(r.device_id); refetchDevices();
    });
  };

  return (
    <>
      <PageHeader crumbs={[{ href: '/customers', label: 'Customers' }, { label: customer()?.name ?? '…' }]} title={customer()?.name ?? '…'} />

      <Show when={customer() && !live()}>
        <p class="mb-6 rounded-xl border-2 border-amber bg-static px-4 py-3 text-sm">
          {customer()!.status === 'closed' ? 'This account is closed: its zones are switched off and their player links no longer work.'
            : customer()!.suspended_reason === 'unpaid' ? 'This account is suspended for non-payment: every zone plays silence until the open invoice is paid.'
            : 'This account is suspended: every zone plays silence until it is reactivated.'}</p>
      </Show>

      <Show when={admin() && customer()}>
        <Account customer={customer()!} manager={manager()} onSaved={(c) => { setCustomer(c); refetchZones(); }} />
        <Billing customer={customer()!} manager={manager()} onSaved={setCustomer} />
      </Show>

      <section aria-label="Zones" class="mb-8">
        <div class="mb-3 flex flex-wrap items-center gap-3">
          <h2 class="wordmark text-xl">Zones</h2>
          <span class="mono text-ink-3">{zones()?.length ?? 0} of {customer()?.zone_limit} on tier {customer()?.tier}</span>
          <Show when={admin() && live()}>
            <form class="ml-auto flex gap-2" onSubmit={createZone}>
              <input class="input input-sm input-bordered" required placeholder="Zone name, e.g. Shop floor" value={zoneName()}
                onInput={(e) => setZoneName(e.currentTarget.value)} aria-label="Zone name" />
              <button class="btn btn-sm btn-primary" disabled={z.busy()}>New zone</button>
            </form>
          </Show>
        </div>
        <ErrorLine error={z.error} />
        <div class="card-z overflow-x-auto p-0">
          <table class="table">
            <thead><tr><th class="th">Zone</th><th class="th">Music</th><th class="th">Volume ceiling</th><th class="th">Status</th></tr></thead>
            <tbody>
              <For each={zones()} fallback={<tr><td colspan="4" class="text-ink-3">No zones yet.</td></tr>}>
                {(zn) => (
                  <tr class="hover">
                    <td><A href={`/zones/${zn.id}`} class="font-semibold hover:text-magenta">{zn.name}</A>
                      <div class="mono text-ink-3">{zn.id.slice(0, 8)}</div></td>
                    <td class="text-ink-2">{music(zn)}</td>
                    <td class="mono">{zn.volume_ceiling}%</td>
                    <td><StatusPill tone={zoneState(zn)[0]}>{zoneState(zn)[1]}</StatusPill></td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </section>

      <Show when={customer()}>
        <CustomerStations customer={customer()!} admin={admin() && live()} onChanged={setCustomer} />
        <CustomerTape customer={customer()!} onChanged={setCustomer} />
      </Show>

      <section aria-label="Devices" class="mb-8">
        <div class="mb-3 flex flex-wrap items-center gap-3">
          <h2 class="wordmark text-xl">Devices</h2>
          <Show when={admin() && (zones() ?? []).length > 0}>
            <form class="ml-auto flex flex-wrap gap-2" onSubmit={pair}>
              <input class="input input-sm input-bordered" placeholder="Device name" value={pairName()} onInput={(e) => setPairName(e.currentTarget.value)} aria-label="Device name" />
              <select class="select select-sm select-bordered" value={pairZone() || zones()?.[0]?.id} onChange={(e) => setPairZone(e.currentTarget.value)} aria-label="Zone">
                <For each={zones()}>{(zn) => <option value={zn.id}>{zn.name}</option>}</For>
              </select>
              <button class="btn btn-sm btn-secondary" disabled={d.busy()}>Pair device</button>
            </form>
          </Show>
        </div>
        <ErrorLine error={d.error} />
        <Show when={shownDevice()}>{(dv) =>
          <PairingPanel device={dv()} zone={zoneOf(dv().zone_id)} busy={d.busy()} onRenew={() => renew(dv().id)} onClose={() => setShown(null)} />}
        </Show>
        <div class="card-z overflow-x-auto p-0">
          <table class="table">
            <thead><tr><th class="th">Device</th><th class="th">Zone</th><th class="th">State</th><th class="th">Last seen</th><th /></tr></thead>
            <tbody>
              <For each={devices()} fallback={<tr><td colspan="5" class="text-ink-3">No devices. Any HLS or Icecast player can also use a zone endpoint directly.</td></tr>}>
                {(dv) => (
                  <tr>
                    <Show when={editing() === dv.id} fallback={<>
                      <td><div class="font-semibold">{dv.name}</div><div class="text-xs text-ink-3">{dv.kind === 'web' ? 'Web player' : dv.kind === 'byod' ? 'Own device' : 'ScaiLabs box'}</div></td>
                      <td class="text-ink-2">{zoneOf(dv.zone_id)}</td>
                    </>}>
                      <td><input class="input input-sm input-bordered w-full" value={devName()} onInput={(e) => setDevName(e.currentTarget.value)} aria-label="Device name" /></td>
                      <td><select class="select select-sm select-bordered" value={devZone()} onChange={(e) => setDevZone(e.currentTarget.value)} aria-label="Device zone">
                        <For each={zones()}>{(zn) => <option value={zn.id}>{zn.name}</option>}</For>
                      </select></td>
                    </Show>
                    <td>
                      <Show when={dv.paired} fallback={
                        <span class="flex flex-wrap items-center gap-2 text-sm">
                          <Show when={dv.pairing_code} fallback={<StatusPill tone="warn">Code expired</StatusPill>}>
                            <StatusPill tone="warn">Waiting to pair</StatusPill>
                            <code class="mono font-bold tracking-widest">{dv.pairing_code}</code>
                          </Show>
                          <Show when={admin()}>
                            <button class="btn btn-ghost btn-xs" disabled={d.busy()}
                              onClick={() => (dv.pairing_code ? setShown(dv.id) : renew(dv.id))}>{dv.pairing_code ? 'Show QR' : 'New code'}</button>
                          </Show>
                        </span>}>
                        <StatusPill tone={dv.online ? 'ok' : 'off'}>{dv.online ? (dv.last_state?.playing ? 'Playing' : 'Online') : 'Offline'}</StatusPill>
                      </Show>
                    </td>
                    <td class="text-sm text-ink-3">{fmtTime(dv.last_seen_at)}</td>
                    <td class="whitespace-nowrap text-right">
                      <Show when={admin()}>
                        <Show when={editing() === dv.id} fallback={<button class="btn btn-ghost btn-xs" onClick={() => editDevice(dv)}>Edit</button>}>
                          <button class="btn btn-primary btn-xs" disabled={d.busy() || !devName()} onClick={() => saveDevice(dv)}>Save</button>
                          <button class="btn btn-ghost btn-xs" onClick={() => setEditing(null)}>Cancel</button>
                        </Show>
                      </Show>
                      <Show when={admin() && dv.paired}>
                        <button class="btn btn-ghost btn-xs" onClick={() => d.run(() => api(`/devices/${dv.id}/commands`, { method: 'POST', json: { command: 'restart' } }))}>Restart</button>
                      </Show>
                      <Show when={admin()}>
                        <button class="btn btn-ghost btn-xs text-error" onClick={() => d.run(async () => { await api(`/devices/${dv.id}`, { method: 'DELETE' }); refetchDevices(); })}>Remove</button>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <p class="mt-2 text-sm text-ink-3">To pair a tablet, scan the QR code with it, or open <span class="mono">scai.fm/pair</span> on it and type the code.
          Unused codes expire after 10 minutes; a device that never pairs is removed after a week. Moving a paired device to another zone switches it over within about 30 seconds.</p>
      </section>

      <Show when={admin()}>
        <section aria-label="Users" class="card-z">
          <h2 class="wordmark mb-2 text-xl">People</h2>
          <Users scope={{ customer_id: p.id }} roles={['customer_admin', 'customer_staff']} />
        </section>
      </Show>
    </>
  );
}

const TIERS = [
  { v: 'small', l: 'Small · up to 3 zones' },
  { v: 'medium', l: 'Medium · up to 10 zones' },
  { v: 'large', l: 'Large · up to 20 zones' },
  { v: 'custom', l: 'Custom' },
];

/** The account itself: everyone with admin rights renames it; the partner and operators set tier and status. */
function Account(props: { customer: Customer; manager: boolean; onSaved: (c: Customer) => void }) {
  const [name, setName] = createSignal('');
  const [tier, setTier] = createSignal('');
  const [limit, setLimit] = createSignal(0);
  createEffect(() => { setName(props.customer.name); setTier(props.customer.tier); setLimit(props.customer.zone_limit); });
  const a = useAction();
  const save = (json: any) => a.run(async () => props.onSaved(await api<Customer>(`/customers/${props.customer.id}`, { method: 'PATCH', json })));
  const tierDirty = () => tier() !== props.customer.tier || (tier() === 'custom' && limit() !== props.customer.zone_limit);
  const st = () => props.customer.status;

  return (
    <section aria-label="Account" class="card-z mb-8">
      <h2 class="wordmark mb-3 text-xl">Account</h2>
      <div class="flex flex-wrap items-end gap-6">
        <form class="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); save({ name: name() }); }}>
          <label class="form-control"><span class="label-text font-semibold">Name</span>
            <input class="input input-sm input-bordered" required value={name()} onInput={(e) => setName(e.currentTarget.value)} /></label>
          <button class="btn btn-sm btn-primary" disabled={a.busy() || name() === props.customer.name}>Rename</button>
        </form>
        <Show when={props.manager} fallback={<p class="text-sm text-ink-3">Tier <b class="capitalize">{props.customer.tier}</b> · {props.customer.zone_limit} zones. Your partner changes the tier.</p>}>
          <form class="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); save({ tier: tier(), zone_limit: tier() === 'custom' ? limit() : undefined }); }}>
            <label class="form-control"><span class="label-text font-semibold">Tier</span>
              <select class="select select-sm select-bordered" value={tier()} onChange={(e) => setTier(e.currentTarget.value)}>
                <For each={TIERS}>{(t) => <option value={t.v}>{t.l}</option>}</For>
              </select></label>
            <Show when={tier() === 'custom'}>
              <label class="form-control w-24"><span class="label-text font-semibold">Zones</span>
                <input type="number" min="1" max="1000" class="input input-sm input-bordered" value={limit()} onInput={(e) => setLimit(Number(e.currentTarget.value))} /></label>
            </Show>
            <button class="btn btn-sm btn-primary" disabled={a.busy() || !tierDirty()}>Change tier</button>
          </form>
        </Show>
      </div>
      <Show when={props.manager}>
        <div class="mt-5 flex flex-wrap items-start gap-3 border-t border-line pt-4">
          <span class="self-center text-sm">Status: <StatusPill tone={st() === 'active' || st() === 'trial' ? 'ok' : 'off'}>{st()}</StatusPill></span>
          <Show when={st() === 'trial'}>
            <button class="btn btn-sm btn-secondary" disabled={a.busy()} onClick={() => save({ status: 'active' })}>End trial: make active</button>
          </Show>
          <Show when={st() === 'suspended' || st() === 'closed'}>
            <button class="btn btn-sm btn-secondary" disabled={a.busy()} onClick={() => save({ status: 'active' })}>Reactivate</button>
          </Show>
          <Show when={st() === 'active' || st() === 'trial'}>
            <ConfirmButton label="Suspend" confirm="Suspend account" busy={a.busy()} onConfirm={() => save({ status: 'suspended' })}
              warning="Every zone of this customer plays silence within about 20 seconds, until you reactivate the account. Nothing is deleted." />
          </Show>
          <Show when={st() !== 'closed'}>
            <ConfirmButton label="Close account" confirm="Close account" typed={props.customer.name} busy={a.busy()} onConfirm={() => save({ status: 'closed' })}
              warning="Closing switches every zone off and revokes all player links and paired devices. Reactivating later does not bring the zones back." />
          </Show>
        </div>
      </Show>
      <ErrorLine error={a.error} />
    </section>
  );
}

const PROFILE_FIELDS: { k: keyof BillingProfile; l: string; w?: string; ph?: string }[] = [
  { k: 'company_name', l: 'Company name', w: 'w-64' },
  { k: 'vat_number', l: 'VAT number', w: 'w-40', ph: 'NL123456789B01' },
  { k: 'invoice_email', l: 'Invoice e-mail', w: 'w-64' },
  { k: 'address_line1', l: 'Address', w: 'w-64' },
  { k: 'address_line2', l: 'Address line 2', w: 'w-64' },
  { k: 'postal_code', l: 'Postal code', w: 'w-28' },
  { k: 'city', l: 'City', w: 'w-44' },
  { k: 'country_code', l: 'Country', w: 'w-20', ph: 'NL' },
];
const PAYMENT: Record<string, ['ok' | 'warn' | 'off' | 'bad', string]> = {
  none: ['off', 'Not billed yet'], ok: ['ok', 'Paid up'], past_due: ['warn', 'Payment failed'], unpaid: ['bad', 'Unpaid: suspended'],
};

/** Billing through ScaiControl: who is invoiced, payment status, the buyer profile (customer mode) and the trial. */
function Billing(props: { customer: Customer; manager: boolean; onSaved: (c: Customer) => void }) {
  const [form, setForm] = createSignal<BillingProfile>({});
  const [trialEnd, setTrialEnd] = createSignal('');
  createEffect(() => {
    setForm({ language: 'nl', ...(props.customer.billing_profile ?? {}) });
    setTrialEnd(props.customer.trial_ends_at ? props.customer.trial_ends_at.slice(0, 10) : '');
  });
  const a = useAction();
  const c = () => props.customer;
  const direct = () => c().billing_mode === 'customer';
  const dirty = () => JSON.stringify(form()) !== JSON.stringify({ language: 'nl', ...(c().billing_profile ?? {}) });
  const save = (json: any) => a.run(async () => props.onSaved(await api<Customer>(`/customers/${c().id}`, { method: 'PATCH', json })));
  const saveProfile = (e: Event) => {
    e.preventDefault();
    const clean = Object.fromEntries(Object.entries(form()).filter(([, v]) => v && String(v).trim()).map(([k, v]) => [k, String(v).trim()]));
    save({ billing_profile: Object.keys(clean).length ? clean : null });
  };

  return (
    <section aria-label="Billing" class="card-z mb-8">
      <div class="mb-3 flex flex-wrap items-center gap-3">
        <h2 class="wordmark text-xl">Billing</h2>
        <StatusPill tone={PAYMENT[c().payment_status][0]}>{PAYMENT[c().payment_status][1]}</StatusPill>
        <span class="text-sm text-ink-3">{direct() ? 'ScaiLabs invoices this customer monthly.' : 'Billed through the partner; the partner invoices this customer.'}</span>
      </div>
      <Show when={c().billing_error}>
        <p class="mb-3 rounded-lg border border-amber bg-static px-3 py-2 text-sm">Billing is not set up yet: {c().billing_error}</p>
      </Show>
      <Show when={c().status === 'trial'}>
        <div class="mb-4 flex flex-wrap items-end gap-2 text-sm">
          <Show when={props.manager} fallback={<p>Trial{c().trial_ends_at ? ` until ${new Date(c().trial_ends_at + 'Z').toLocaleDateString()}` : ''}; billing starts when it ends.</p>}>
            <label class="form-control"><span class="label-text font-semibold">Trial ends (billing starts)</span>
              <input type="date" class="input input-sm input-bordered" value={trialEnd()} onInput={(e) => setTrialEnd(e.currentTarget.value)} /></label>
            <button class="btn btn-sm btn-primary" disabled={a.busy() || trialEnd() === (c().trial_ends_at ?? '').slice(0, 10)}
              onClick={() => save({ trial_ends_at: trialEnd() ? `${trialEnd()}T00:00:00Z` : null })}>Set</button>
          </Show>
        </div>
      </Show>
      <Show when={direct()} fallback={<p class="text-sm text-ink-3">No billing profile needed for this customer.</p>}>
        <form class="flex flex-col gap-3" onSubmit={saveProfile}>
          <p class="text-sm text-ink-3">Invoice details{c().billing_profile_missing.length ? ` · still missing: ${c().billing_profile_missing.map((m) => PROFILE_FIELDS.find((f) => f.k === m)?.l ?? m).join(', ')}` : ''}</p>
          <div class="flex flex-wrap gap-3">
            <For each={PROFILE_FIELDS}>
              {(f) => (
                <label class="form-control"><span class="label-text font-semibold">{f.l}</span>
                  <input class={`input input-sm input-bordered ${f.w ?? ''}`} placeholder={f.ph} value={form()[f.k] ?? ''}
                    onInput={(e) => setForm({ ...form(), [f.k]: f.k === 'country_code' || f.k === 'vat_number' ? e.currentTarget.value.toUpperCase().replace(/\s/g, '') : e.currentTarget.value })} /></label>
              )}
            </For>
            <label class="form-control"><span class="label-text font-semibold">Invoice language</span>
              <select class="select select-sm select-bordered" value={form().language ?? 'nl'} onChange={(e) => setForm({ ...form(), language: e.currentTarget.value })}>
                <option value="nl">Dutch</option><option value="en">English</option><option value="de">German</option><option value="fr">French</option>
              </select></label>
          </div>
          <div><button class="btn btn-sm btn-primary" disabled={a.busy() || !dirty()}>Save invoice details</button></div>
        </form>
      </Show>
      <ErrorLine error={a.error} />
    </section>
  );
}
